/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * PositionDisplay - Display de coordenadas GPS (Lat/Lon)
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';
import { MapPin } from 'lucide-react';

const PositionDisplay = ({
  value = { latitude: 0, longitude: 0 },
  label = '',
  showLabel = true,
  decimalPlaces = 5,
  format = 'decimal', // 'decimal', 'dms' (degrees/minutes/seconds)
  showIcon = true,
}) => {
  // Formatar coordenadas
  const formattedCoords = useMemo(() => {
    if (!value || typeof value !== 'object') {
      return { lat: '--', lon: '--' };
    }

    const lat = value.latitude ?? value.lat ?? null;
    const lon = value.longitude ?? value.lon ?? null;

    if (lat === null || lon === null) {
      return { lat: '--', lon: '--' };
    }

    if (format === 'dms') {
      // Formato Graus/Minutos/Segundos
      const toDMS = (coord, isLat) => {
        const absolute = Math.abs(coord);
        const degrees = Math.floor(absolute);
        const minutesFloat = (absolute - degrees) * 60;
        const minutes = Math.floor(minutesFloat);
        const seconds = ((minutesFloat - minutes) * 60).toFixed(1);
        const direction = isLat 
          ? (coord >= 0 ? 'N' : 'S')
          : (coord >= 0 ? 'E' : 'W');
        return `${degrees}° ${minutes}' ${seconds}" ${direction}`;
      };

      return {
        lat: toDMS(lat, true),
        lon: toDMS(lon, false),
      };
    }

    // Formato decimal padrão
    const latDir = lat >= 0 ? 'N' : 'S';
    const lonDir = lon >= 0 ? 'E' : 'W';
    
    return {
      lat: `${Math.abs(lat).toFixed(decimalPlaces)}° ${latDir}`,
      lon: `${Math.abs(lon).toFixed(decimalPlaces)}° ${lonDir}`,
    };
  }, [value, format, decimalPlaces]);

  return (
    <div className="flex flex-col gap-2 p-4 bg-gray-900 rounded-lg border border-gray-700 min-w-[200px]">
      {/* Header com label e ícone */}
      <div className="flex items-center gap-2 mb-1">
        {showIcon && (
          <MapPin className="w-4 h-4 text-blue-400" />
        )}
        {showLabel && label && (
          <span className="text-gray-400 text-sm font-medium">
            {label}
          </span>
        )}
        {!label && showLabel && (
          <span className="text-gray-400 text-sm font-medium">
            Posição
          </span>
        )}
      </div>

      {/* Latitude */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-500 text-xs font-medium uppercase">
          LAT
        </span>
        <span 
          className="font-mono text-green-400 text-sm"
          style={{ textShadow: '0 0 8px rgba(34, 197, 94, 0.3)' }}
        >
          {formattedCoords.lat}
        </span>
      </div>

      {/* Longitude */}
      <div className="flex items-center justify-between gap-4">
        <span className="text-gray-500 text-xs font-medium uppercase">
          LON
        </span>
        <span 
          className="font-mono text-green-400 text-sm"
          style={{ textShadow: '0 0 8px rgba(34, 197, 94, 0.3)' }}
        >
          {formattedCoords.lon}
        </span>
      </div>

      {/* Indicador de GPS ativo */}
      <div className="flex items-center gap-2 mt-1 pt-2 border-t border-gray-800">
        <div 
          className={`w-2 h-2 rounded-full ${
            formattedCoords.lat !== '--' ? 'bg-green-500 animate-pulse' : 'bg-gray-600'
          }`}
        />
        <span className="text-gray-500 text-xs">
          {formattedCoords.lat !== '--' ? 'GPS Ativo' : 'Sem Sinal'}
        </span>
      </div>
    </div>
  );
};

export default PositionDisplay;
